import { RefObject } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ID, isNode, Node, Edge } from './GraphData'
import GraphData from './GraphData'
import modal from './Modal'
import * as cst from './cst'
import { network } from 'vis-network'

export const moveToNode = (node : ID, moving: boolean, network, zoom : boolean = false) : void => {
  const focusedElement = document.activeElement as HTMLElement
  if (focusedElement) {
    focusedElement.blur()
  }
  if(moving){
    return
  }
  moving = true
  if(zoom){
    network.focus(node, {scale:1.5, animation:{duration:150, easingFunction:"easeInOutCubic"}}) //Weird bug in vis. Not checking for "Moving" can kill the view.
  }
  else{
    network.focus(node, {animation:{duration:150, easingFunction:"easeInOutCubic"}}) //Weird bug in vis. Not checking for "Moving" can kill the view.
  }
  setTimeout(()=>{moving = false; network.focus(node)}, 150)
}

export const handleArrowNavigation = (key : string, selectedNode: ID, graphData : GraphData, network : any) : ID => {
  if (key === 'ArrowUp') {
    const parentNodeId = graphData.getParent(selectedNode)
    if (parentNodeId) {
      network.selectNodes([parentNodeId]);
    }
  } else if (key === 'ArrowDown') {
    if(graphData.getNode(selectedNode).collapsed){
      return
    }
    const childNodeId = graphData.getChild(selectedNode)
    if (childNodeId) {
      network.selectNodes([childNodeId]);
    }
  } else if (key === 'ArrowRight') {
    const right_neighbor = graphData.getRightNeighbor(selectedNode)
    if(right_neighbor){
        network.selectNodes([right_neighbor])
      }
  } else if (key === 'ArrowLeft') {
    const left_neighbor = graphData.getLeftNeighbor(selectedNode)
    if(left_neighbor){
        network.selectNodes([left_neighbor])
      }
  }

  let selection = network.getSelection()
  if(selection.nodes.length == 1){
      selectedNode = selection.nodes[0]
  } else return
  return selectedNode

}

export const jumpByNumber = (key : string, filteredNodesRef : any, graphData: GraphData, network : any, reloadGraph : Function) => {
  const node = filteredNodesRef.current[parseInt(key)-1]
  if(!node){
    return
  }
  let nodeId = node.id
  network.selectNodes([nodeId])
  if(graphData.uncollapseParentTree(nodeId)){
    reloadGraph()
  }
  return nodeId
}

export const jumpToStart = (graphData : GraphData, network : any) => {
  const selection = network.getSelection()
  let selectedNode : ID
  if(selection.nodes.length == 1){
      selectedNode = selection.nodes[0]
  }
  let toBeSelected : ID
  if(!selectedNode){ //If nothing selected, go to first root
    toBeSelected = graphData.getRoots()[0]
  }
  else{ //Else start of line
    const level = graphData.getLevelOfNode(selectedNode)
    toBeSelected = graphData.getLevel(graphData.getRoot(selectedNode), level, false)[0]
    if (toBeSelected == selectedNode){
      toBeSelected = graphData.getRoot(selectedNode)
    }
  }
  network.selectNodes([toBeSelected])
  return toBeSelected 
}

export const jumpToEnd = (graphData : GraphData, network : any) => {
  const selection = network.getSelection()
  let selectedNode : ID
  if(selection.nodes.length == 1){
      selectedNode = selection.nodes[0]
  }
  let toBeSelected : ID
  if(!selectedNode){
    return
  }
  else{
    const level = graphData.getLevelOfNode(selectedNode)
    const levelNodes = graphData.getLevel(graphData.getRoot(selectedNode), level, false)
    toBeSelected = levelNodes[levelNodes.length - 1]
  }
  network.selectNodes([toBeSelected])
  return toBeSelected
}

export const swapToLeft = (selectedNode : ID, graphData : GraphData) => {
  graphData.swapNeighbor(selectedNode, true)
  return selectedNode
}
export const swapToRight = (selectedNode : ID, graphData : GraphData) => {
  graphData.swapNeighbor(selectedNode, false)
  return selectedNode
}

export const swapToStart = (selectedNode : ID, graphData : GraphData) => {
  graphData.moveNodeToEnd(selectedNode, true)
  return selectedNode
}

export const swapToEnd = (selectedNode : ID, graphData : GraphData) => {
  graphData.moveNodeToEnd(selectedNode, false)
  return selectedNode
}

export const selectSubTree = (selectedNode : ID, graphData: GraphData, network : any) => {
  const subtree = graphData.getSubTree(selectedNode) //TODO foreach...
  network.selectNodes(subtree)
  return
}

export const toggleCollapsed = (selectedNode : ID, graphData : GraphData) => {
  const node = graphData.getNode(selectedNode)
  if (!node){
    return
  }
  if(node.deps_max == 0){
    return
  }
  graphData.toggleCollapsed(selectedNode)
}

export const deleteSelection = async (
  graphData : GraphData, 
  network : any, 
  setModalUp : Function, 
  clearHeldKeys : Function, 
  preMod : Function, 
  postMod : Function
  ) : Promise<ID> => {

  const selection = network.getSelection()
  const toDel = selection.nodes.length //TODO handle edges
  const delOneEdge = (toDel == 0 && selection.edges.length == 1)
  const msg = delOneEdge ? "Delete the edge?" : `Delete ${toDel} nodes?`
  let ret : ID
  clearHeldKeys()
  setModalUp(true)
  await modal(
    cst.MODAL_CONFIRM,
    msg
  ).then(async (val)=>{
    if(val == true){
      await preMod()
      const parent = (graphData.guessCommonParent(selection.nodes))
      if(delOneEdge){
        graphData.removeEdge(selection.edges[0])
      }
      else{
        graphData.removeNodes(selection.nodes)
      }
      await postMod()
      if(parent){
        network.selectNodes([parent])
        ret = parent
      }
    }
  }).finally(()=>setModalUp(false))
  return ret
}

export const newEdgeActivate = (
  network: any, 
  newEdgeActiveRef : RefObject<boolean>, 
  setNewEdgeActive : Function, 
  setHighlightedNodes : Function,
  reloadGraph : Function
  ) => { 
  // = What happens when user presses "a"
  if(newEdgeActiveRef.current == true){
    return
  }
  const selection = network.getSelection()
  let selectedNode : ID
  let ret : ID
  if(selection.nodes.length == 1){
      selectedNode = selection.nodes[0]
  }
  //TODO for now, just one is to be selected at a time
  if(!selectedNode){
    return
  }
  setHighlightedNodes([selectedNode])
  network.selectNodes([])
  setNewEdgeActive(true)
  reloadGraph()
}

export const createNewEdge = (
  graphData : GraphData,
  from : Array<ID>, 
  to: ID, 
  setNewEdgeActive : Function, 
  setHighlightedNodes : Function,
  reloadGraph : Function) => { //What happens when user clicks node in edit mode
  //TODO for now just 1 in "from"
    
  const filtered = graphData.getEdges().filter(e=>e.from === from[0] && e.to == to)
  if(filtered.length){ //Edge already exists
    return
  }

  const newEdge : Edge = { id: uuidv4(), from: from[0], to:to}
  if (!graphData.checkDirectedCycle(newEdge)){
    graphData.addEdge(newEdge)
    setNewEdgeActive(false)
    setHighlightedNodes([])
  }
}

export const newNode = async(
  graphData : GraphData, 
  network : any, 
  setModalUp : Function, 
  clearHeldKeys : Function, 
  preMod : Function, 
  postMod : Function
  ) : Promise<ID> => {

  const selection = network.getSelection()
  let selectedNode : ID
  let ret : ID
  if(selection.nodes.length == 1){
      selectedNode = selection.nodes[0]
  }
  if(selectedNode){
    clearHeldKeys()
    setModalUp(true)
    await modal(
      cst.MODAL_FORM,
      "New Child Node", //TODO first 5 words of label
      {"title":{type:"string"},"description":{type:"textarea"},"owner":{type:"string"}}
    ).then(async val=>{
      if(val != undefined && isNode(val)){
        await preMod()
        graphData.addChildNode(selectedNode,val)
        await postMod()
      }
      else{
        return
      }
    }).finally(()=>setModalUp(false))
  } else if (!selection.length){
    clearHeldKeys()
    setModalUp(true)
    await modal(
      cst.MODAL_FORM,
      "New Root Node",
      {"title":{type:"string"},"description":{type:"textarea"},"owner":{type:"string"}}
    ).then(async val=>{
      if(val != undefined && isNode(val)){
        //TODO mod function
        await preMod()
        const id = graphData.addNode(val)
        await postMod()
        network.selectNodes([id])
        ret = id
      }
      else{
        return
      }
    }).finally(()=>setModalUp(false))
  }
  return ret

}

export const editNode = async(
  graphData : GraphData, 
  network : any, 
  setModalUp : Function, 
  clearHeldKeys : Function, 
  preMod : Function, 
  postMod : Function
  ) : Promise<void> => {

  const selection = network.getSelection()
  let selectedNode : ID
  if(selection.nodes.length == 1){
      selectedNode = selection.nodes[0]
  }
  if(!selectedNode){
    return
  }
  clearHeldKeys()
  setModalUp(true)
  const node = graphData.getNode(selectedNode)
  let title = ""
  if(node.title.length > 23){
    title = node.title.slice(0,20) + "..."
  }
  else{
    title = node.title
  }
  modal(
    cst.MODAL_FORM,
    `Editing "${title}"`, //TODO first 5 words of label
    {
      "title":{type:"string", value:node.title},
      "description":{type:"textarea", value:node.description},
      "owner":{type:"string", value:node.owner}
    }

  ).then(async val=>{
    if(val != undefined && isNode(val)){
      await preMod()
      graphData.updateNode(selectedNode,val)
      await postMod()
    }
    else{
      return
    }
  }).finally(()=>setModalUp(false))
}

export const helpModal = (setModalUp : Function) => {
  setModalUp(true)
  modal(
    cst.MODAL_INFO,
    "Help",
    cst.HELP
  ).finally(()=>setModalUp(false))
}
